import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import { useCapitalGlobalCoveragesCatalog } from '@/features/quotes/hooks/capital-global/use-capital-global-coverages-catalog.hook';
import { useSubmitCapitalGlobalCoverages } from '@/features/quotes/hooks/capital-global/use-submit-capital-global-coverages.hook';
import { useProposalDetails } from '@/features/quotes/hooks/homeowner/use-proposal-details.hook';
import {
  capCapitalAndPercent,
  commitPercentAndCapital,
  getCapitalOutOfRangeMessage,
  getCoverageCapitalsByCode,
  getEffectiveLimits,
  getInitialCoverageValues,
  getPercentReferenceCapital,
  isEffectiveRangeInvalid,
  normalizeCoverageCode,
  previewCapitalFromPercentDraft,
  roundCoveragePercent,
  type CoverageLimitsContext,
} from '@/features/quotes/schemas/capital-global/coverage-limits.util';
import {
  findCoverageRelationConflicts,
  findDependentCapitalConflicts,
} from '@/features/quotes/schemas/capital-global/coverage-relations.util';
import type {
  CapitalGlobalAssistanceCatalogItem,
  CapitalGlobalCoverageCatalogItem,
  CapitalGlobalSelectOption,
} from '@/features/quotes/schemas/capital-global/coverages-catalog';
import {
  findBenefitOption,
  findMorteCoverage,
  isMorteCoverageId,
  usesBenefitSelection,
  usesBenefitSelectionWithPercent,
} from '@/features/quotes/schemas/capital-global/coverages-catalog';
import {
  findCapitalGlobalExclusionConflicts,
  routeExclusionConflict,
} from '@/features/quotes/schemas/capital-global/exclusion-group-conflicts.util';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  createStepCoveragesFormSchema,
  type StepCoveragesFormValues,
  type StepCoveragesSchemaOptions,
} from '@/features/quotes/schemas/capital-global/step-coverages.schema';
import type { MapAttachValidationToCoverageErrorsResult } from '@/features/quotes/utils/map-attach-validation-to-coverage-errors.util';
import { formatCurrency } from '@/shared/utils/currencyFormatter';
import { removeFormatRegisterNumber } from '@/shared/utils/remove-format-register-number';
import type {
  CoveragesDataMap,
  CoveragesEntityValues,
  EntityTab,
} from '../../types';
import type { AssistanceState } from '../sections/assistance-toggles.component';
import type {
  CoverageFieldErrors,
  CoverageState,
} from '../sections/coverages-table.component';
import {
  buildStepCoveragesErrors,
  collectStepCoveragesErrorMessages,
  type StepCoveragesVisibleErrors,
} from '../utils/build-step-coverages-errors.util';
import {
  getGroupReferenceCapitals,
  getReferenceCapitalForEntity,
  isUniformeColaboradoresOnly,
} from '../utils/map-api.util';

type StepCoveragesFormApi = {
  state: {
    values: StepCoveragesFormValues;
  };
  setFieldValue: (field: string, value: unknown) => void;
};

type UseStepCoveragesViewModelParams = {
  form: unknown;
  onNext: () => void;
  /** Reference individual capital for the active entity (from step 2 risk data). */
  referenceCapital: number;
  activeEntityId: string;
  entities: EntityTab[];
  handleEntityTabChange: (newEntityId: string) => void;
  /** Callback to capture all entities' form state at submit time. */
  captureAllEntities: () => CoveragesDataMap;
  /** Writes validated/committed values back into the per-entity state. */
  applyEntityValues: (values: CoveragesDataMap) => void;
};

type UseStepCoveragesViewModelResult = {
  coverageCatalog: CapitalGlobalCoverageCatalogItem[];
  assistanceCatalog: CapitalGlobalAssistanceCatalogItem[];
  assistanceFuneralOptions: CapitalGlobalSelectOption[];
  capitalizationOptions: CapitalGlobalSelectOption[];
  /** `false` quando a entidade ativa tem apenas colaboradores (sem sócios). */
  isCapitalizationAvailable: boolean;
  isCatalogLoading: boolean;
  hasCatalogError: boolean;
  retryCatalog: () => void;
  morteId: string | undefined;
  handleToggleCoverage: (id: string, enabled: boolean) => void;
  handleIndividualCapitalChange: (id: string, value: string) => void;
  handleBenefitChange: (id: string, benefitCode: string) => void;
  handlePercentChange: (id: string, value: string) => void;
  handlePercentBlur: (id: string) => void;
  handleToggleAssistance: (code: string, enabled: boolean) => void;
  handleCapitalizationChange: (value: string) => void;
  handleAssistanceFuneralChange: (value: string) => void;
  handleSubmit: () => void;
  handleEntityTabChangeWithReset: (newEntityId: string) => void;
  isPending: boolean;
  getVisibleErrors: (
    values: StepCoveragesFormValues
  ) => StepCoveragesVisibleErrors;
};

// ─── helpers ────────────────────────────────────────────────────────────────

function parseCurrencyToNumber(raw: string): number {
  return parseInt(String(raw).replace(/\D/g, ''), 10) / 100;
}

function commitCoveragesPercentDrafts(
  coverages: Record<string, CoverageState>,
  catalogById: Map<string, CapitalGlobalCoverageCatalogItem>,
  limitsCtx: CoverageLimitsContext,
  morteId: string | undefined
): Record<string, CoverageState> {
  const updated: Record<string, CoverageState> = { ...coverages };

  for (const [id, row] of Object.entries(updated)) {
    if (!row?.enabled || isMorteCoverageId(id, morteId)) continue;

    const catalogItem = catalogById.get(id);
    if (!catalogItem) continue;

    // Linha com benefício de valor em R$ escolhido não tem rascunho de % para
    // comitar — o capital é o da opção. Nas diárias (WITH_SI) o % é o input do
    // valor da diária, então comita normalmente.
    if (
      findBenefitOption(catalogItem, row.benefitCode) &&
      !usesBenefitSelectionWithPercent(catalogItem)
    ) {
      continue;
    }

    const committed = commitPercentAndCapital(
      row.percent,
      catalogItem,
      limitsCtx,
      formatCurrency,
      parseCurrencyToNumber(row.maxIndividualCapital)
    );
    updated[id] = { ...row, ...committed };
  }

  return updated;
}

/**
 * Contexto de limites de UMA entidade — fonte ÚNICA para os três consumidores
 * (tela, validação de submit e montagem do payload).
 *
 * Ter cópias divergentes era o bug: o caminho multi-estipulante montava o
 * contexto sem `coverageCapitalsByCode`, então toda cobertura
 * `PROPORTIONAL_TO_COVERAGE` ficava sem base, era zerada por
 * `commitPercentAndCapital` e o schema acusava "informe o capital" num campo que
 * a tela mostrava preenchido.
 */
function buildEntityLimitsContext(params: {
  coverageCatalog: CapitalGlobalCoverageCatalogItem[];
  morteCatalogItem: CapitalGlobalCoverageCatalogItem | undefined;
  morteId: string | undefined;
  referenceCapital: number;
  morteCapital: number;
  rows: Record<string, CoverageState>;
}): CoverageLimitsContext {
  const {
    coverageCatalog,
    morteCatalogItem,
    morteId,
    referenceCapital,
    morteCapital,
    rows,
  } = params;

  const coverageCapitalsByCode = getCoverageCapitalsByCode(
    coverageCatalog,
    rows
  );

  // Morte entra também pelo capital de referência: a linha dela pode estar sem
  // string formatada (hidratação/resync) e as proporcionais dependem dessa base.
  if (morteCatalogItem && morteCapital > 0) {
    for (const key of [
      morteCatalogItem.code,
      normalizeCoverageCode(morteCatalogItem.code),
      morteCatalogItem.id,
      String(morteCatalogItem.productElementId),
    ]) {
      coverageCapitalsByCode.set(key, morteCapital);
    }
  }

  return { referenceCapital, morteCapital, morteId, coverageCapitalsByCode };
}

type CapitalGlobalCatalogs = {
  coverageCatalog: CapitalGlobalCoverageCatalogItem[];
  assistanceCatalog: CapitalGlobalAssistanceCatalogItem[];
  assistanceFuneralOptions: CapitalGlobalSelectOption[];
  capitalizationOptions: CapitalGlobalSelectOption[];
};

/**
 * Uniforme + apenas colaboradores: coberturas exclusivas de sócio não se
 * aplicam. O filtro é POR ENTIDADE — aplicar o da aba ativa a todos os
 * estipulantes descartava silenciosamente coberturas de sócio dos demais.
 *
 * Capitalização só é contratável com sócios (regra da subscrição): sem
 * opções, a seção some da tela e o conflito de exclusão não a considera.
 */
function filterCatalogsForRiskProfile(
  raw: CapitalGlobalCatalogs,
  riskProfile: ReturnType<typeof findRiskProfileForEntity>
): CapitalGlobalCatalogs {
  if (!isUniformeColaboradoresOnly(riskProfile)) return raw;

  return {
    coverageCatalog: raw.coverageCatalog.filter(item => !item.partnerOnly),
    assistanceCatalog: raw.assistanceCatalog.filter(item => !item.partnerOnly),
    assistanceFuneralOptions: raw.assistanceFuneralOptions.filter(
      item => !item.partnerOnly
    ),
    capitalizationOptions: [],
  };
}

function buildCatalogById(
  coverageCatalog: CapitalGlobalCoverageCatalogItem[]
): Map<string, CapitalGlobalCoverageCatalogItem> {
  return new Map(coverageCatalog.map(item => [item.id, item]));
}

function normalizeEntityDoc(id: string): string {
  return removeFormatRegisterNumber(id) || id;
}

function resolveMorteCapitalForValidation(
  values: CoveragesEntityValues,
  morteId: string | undefined,
  entityReferenceCapital: number
): number {
  if (!morteId) return entityReferenceCapital;
  const raw = values.coverages[morteId]?.maxIndividualCapital ?? '';
  const parsed = parseCurrencyToNumber(raw);
  return Number.isFinite(parsed) && parsed > 0
    ? parsed
    : entityReferenceCapital;
}

function findRiskProfileForEntity(
  proposalDetails: ReturnType<typeof useProposalDetails>['data'],
  entityId: string
) {
  const cleanTarget = normalizeEntityDoc(entityId);
  return proposalDetails?.riskProfileList?.find(
    p => normalizeEntityDoc(p.documentNumber) === cleanTarget
  );
}

type EntityCoveragesValidation = {
  valid: boolean;
  messages: string[];
};

function validateEntityCoverages(
  values: CoveragesEntityValues | undefined,
  entityReferenceCapital: number,
  coverageCatalog: CapitalGlobalCoverageCatalogItem[],
  morteId: string | undefined,
  groupCapitals: ReturnType<typeof getGroupReferenceCapitals>,
  exclusionCatalogs: StepCoveragesSchemaOptions['exclusionCatalogs']
): EntityCoveragesValidation {
  if (!values || entityReferenceCapital <= 0) {
    return {
      valid: false,
      messages: [
        'Não foi possível validar as coberturas. Revise os dados do risco e tente novamente.',
      ],
    };
  }

  const morteCapital = resolveMorteCapitalForValidation(
    values,
    morteId,
    entityReferenceCapital
  );
  const schema = createStepCoveragesFormSchema(
    coverageCatalog,
    morteId,
    entityReferenceCapital,
    morteCapital,
    { groupCapitals, exclusionCatalogs }
  );
  const result = schema.safeParse(values);
  if (result.success) return { valid: true, messages: [] };

  return {
    valid: false,
    messages: collectStepCoveragesErrorMessages(result.error.issues),
  };
}

// ─── catalog merge helpers ───────────────────────────────────────────────────

function mergeCoverageState(
  catalog: CapitalGlobalCoverageCatalogItem[],
  prev: Record<string, CoverageState>,
  morteId: string | undefined
): Record<string, CoverageState> {
  const next: Record<string, CoverageState> = {};
  for (const coverage of catalog) {
    const existing = prev[coverage.id];
    const isMorte = isMorteCoverageId(coverage.id, morteId);
    next[coverage.id] = existing
      ? {
          ...existing,
          enabled: isMorte ? true : existing.enabled,
        }
      : {
          enabled: isMorte,
          maxIndividualCapital: '',
          percent: '',
          benefitCode: '',
        };
  }
  return next;
}

function mergeAssistanceState(
  catalog: CapitalGlobalAssistanceCatalogItem[],
  prev: Record<string, AssistanceState>
): Record<string, AssistanceState> {
  const next: Record<string, AssistanceState> = {};
  for (const assistance of catalog) {
    const existing = prev[assistance.code];
    next[assistance.code] = existing ?? { enabled: false };
  }
  return next;
}

function toCoverageFieldErrorsFromApi(
  apiCoverageErrors: Record<string, string>
): CoverageFieldErrors {
  return Object.fromEntries(
    Object.entries(apiCoverageErrors).map(([id, message]) => [
      id,
      { selection: message },
    ])
  );
}

function mergeCoverageFieldErrors(
  zodErrors: CoverageFieldErrors,
  apiErrors: CoverageFieldErrors
): CoverageFieldErrors {
  const merged: CoverageFieldErrors = { ...zodErrors };

  for (const [id, apiRow] of Object.entries(apiErrors)) {
    merged[id] = { ...merged[id], ...apiRow };
  }

  return merged;
}

// ─── hook ───────────────────────────────────────────────────────────────────

export function useStepCoveragesViewModel({
  form,
  onNext,
  referenceCapital,
  activeEntityId,
  entities,
  handleEntityTabChange,
  captureAllEntities,
  applyEntityValues,
}: UseStepCoveragesViewModelParams): UseStepCoveragesViewModelResult {
  const formApi = form as StepCoveragesFormApi;
  const { policyId, id } = useQuoteParams();
  const resolvedPolicyId = (policyId || id || '').trim();
  const { data: proposalDetails } = useProposalDetails(
    resolvedPolicyId || undefined
  );

  const {
    coverageCatalog: rawCoverageCatalog,
    assistanceCatalog: rawAssistanceCatalog,
    assistanceFuneralOptions: rawAssistanceFuneralOptions,
    capitalizationOptions: rawCapitalizationOptions,
    assistanceUmbrella,
    capitalizationUmbrella,
    isLoading: isCatalogLoading,
    isError: isCatalogError,
    refetch: refetchCatalog,
  } = useCapitalGlobalCoveragesCatalog();

  const rawCatalogs = useMemo<CapitalGlobalCatalogs>(
    () => ({
      coverageCatalog: rawCoverageCatalog,
      assistanceCatalog: rawAssistanceCatalog,
      assistanceFuneralOptions: rawAssistanceFuneralOptions,
      capitalizationOptions: rawCapitalizationOptions,
    }),
    [
      rawCoverageCatalog,
      rawAssistanceCatalog,
      rawAssistanceFuneralOptions,
      rawCapitalizationOptions,
    ]
  );

  /**
   * Catálogos de uma entidade qualquer — usado nos laços que percorrem TODOS os
   * estipulantes (validação e payload), onde o perfil da aba ativa não vale.
   */
  const getCatalogsForEntity = useCallback(
    (entityId: string): CapitalGlobalCatalogs =>
      filterCatalogsForRiskProfile(
        rawCatalogs,
        findRiskProfileForEntity(proposalDetails, entityId)
      ),
    [rawCatalogs, proposalDetails]
  );

  // Catálogos da aba ativa: o que a TELA mostra. Aqui o filtro por
  // `activeEntityId` é o comportamento correto.
  const activeCatalogs = useMemo(
    () => getCatalogsForEntity(activeEntityId),
    [getCatalogsForEntity, activeEntityId]
  );
  const {
    coverageCatalog,
    assistanceCatalog,
    assistanceFuneralOptions,
    capitalizationOptions,
  } = activeCatalogs;

  // Morte sai do catálogo CRU: nunca é exclusiva de sócio, então não deve
  // depender de qual aba está aberta.
  const morteCatalogItem = useMemo(
    () => findMorteCoverage(rawCoverageCatalog),
    [rawCoverageCatalog]
  );
  const morteId = morteCatalogItem?.id;

  const getMorteCapital = useCallback((): number => {
    if (!morteId) return referenceCapital;
    const raw =
      formApi.state.values.coverages[morteId]?.maxIndividualCapital ?? '';
    const parsed = parseCurrencyToNumber(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : referenceCapital;
  }, [morteId, referenceCapital, formApi]);

  const getLimitsContext = useCallback(
    (): CoverageLimitsContext =>
      buildEntityLimitsContext({
        coverageCatalog,
        morteCatalogItem,
        morteId,
        referenceCapital,
        morteCapital: getMorteCapital(),
        rows: formApi.state.values.coverages,
      }),
    [
      referenceCapital,
      getMorteCapital,
      morteId,
      morteCatalogItem,
      coverageCatalog,
      formApi,
    ]
  );

  const catalogById = useMemo(
    () => buildCatalogById(coverageCatalog),
    [coverageCatalog]
  );

  const previousCoverageCatalogSignatureRef = useRef<string>('');
  const previousAssistanceCatalogSignatureRef = useRef<string>('');

  const coverageCatalogSignature = useMemo(
    () =>
      coverageCatalog
        .map(item => `${item.id}:${morteId === item.id ? 'morte' : 'opt'}`)
        .join('|'),
    [coverageCatalog, morteId]
  );

  const assistanceCatalogSignature = useMemo(
    () => assistanceCatalog.map(item => item.code).join('|'),
    [assistanceCatalog]
  );

  useEffect(() => {
    if (
      previousCoverageCatalogSignatureRef.current === coverageCatalogSignature
    ) {
      return;
    }
    previousCoverageCatalogSignatureRef.current = coverageCatalogSignature;

    const merged = mergeCoverageState(
      coverageCatalog,
      formApi.state.values.coverages,
      morteId
    );
    formApi.setFieldValue('coverages', merged);
  }, [
    coverageCatalog,
    coverageCatalogSignature,
    morteId,
    formApi.setFieldValue,
  ]);

  useEffect(() => {
    if (
      previousAssistanceCatalogSignatureRef.current ===
      assistanceCatalogSignature
    ) {
      return;
    }
    previousAssistanceCatalogSignatureRef.current = assistanceCatalogSignature;

    const merged = mergeAssistanceState(
      assistanceCatalog,
      formApi.state.values.assistances
    );
    formApi.setFieldValue('assistances', merged);
  }, [assistanceCatalog, assistanceCatalogSignature, formApi.setFieldValue]);

  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [apiCoverageErrors, setApiCoverageErrors] =
    useState<CoverageFieldErrors>({});
  const [generalApiError, setGeneralApiError] = useState<string | undefined>();

  const clearAttachValidationErrors = useCallback(() => {
    setApiCoverageErrors({});
    setGeneralApiError(undefined);
  }, []);

  const handleAttachValidationError = useCallback(
    (result: MapAttachValidationToCoverageErrorsResult) => {
      setApiCoverageErrors(toCoverageFieldErrorsFromApi(result.coverageErrors));
      setGeneralApiError(result.generalMessage?.trim() || undefined);
    },
    []
  );

  const getCoveragesMapForSubmit = useCallback((): CoveragesDataMap => {
    const map = captureAllEntities();
    const result: CoveragesDataMap = { ...map };

    for (const [doc, values] of Object.entries(result)) {
      const entity =
        entities.find(e => normalizeEntityDoc(e.id) === doc) ??
        entities.find(e => e.id === doc);
      const entityId = entity?.id ?? doc;
      const entityCatalogs = getCatalogsForEntity(entityId);
      const entityReferenceCapital = getReferenceCapitalForEntity(
        proposalDetails,
        entityId
      );
      const entityMorteCapital = resolveMorteCapitalForValidation(
        values,
        morteId,
        entityReferenceCapital
      );

      result[doc] = {
        ...values,
        coverages: commitCoveragesPercentDrafts(
          values.coverages,
          buildCatalogById(entityCatalogs.coverageCatalog),
          buildEntityLimitsContext({
            coverageCatalog: entityCatalogs.coverageCatalog,
            morteCatalogItem,
            morteId,
            referenceCapital: entityReferenceCapital,
            morteCapital: entityMorteCapital,
            rows: values.coverages,
          }),
          morteId
        ),
      };
    }

    return result;
  }, [
    captureAllEntities,
    entities,
    proposalDetails,
    getCatalogsForEntity,
    morteCatalogItem,
    morteId,
  ]);

  const { handleSubmit: submitToApi, isPending } =
    useSubmitCapitalGlobalCoverages({
      onNext,
      getCoveragesMap: getCoveragesMapForSubmit,
      // Catálogo CRU: o filtro de coberturas exclusivas de sócio é resolvido por
      // estipulante dentro do builder do payload. Passar o da aba ativa
      // descartava as coberturas de sócio dos outros estipulantes.
      coverageCatalog: rawCoverageCatalog,
      assistanceUmbrella,
      capitalizationUmbrella,
      morteId,
      onAttachValidationError: handleAttachValidationError,
    });

  const activeRiskProfile = useMemo(
    () => findRiskProfileForEntity(proposalDetails, activeEntityId),
    [proposalDetails, activeEntityId]
  );

  const activeGroupCapitals = useMemo(
    () => getGroupReferenceCapitals(activeRiskProfile),
    [activeRiskProfile]
  );

  const isCapitalizationAvailable =
    !isUniformeColaboradoresOnly(activeRiskProfile);

  /**
   * O toggle NÃO mexe em outras coberturas. Conflito de exclusividade e
   * dependência quebrada são acusados como ERRO na linha (mesmo canal que as
   * mensagens de validação do InsureMO usam) — o corretor decide o que
   * desligar, em vez de a tela desmarcar algo por conta própria.
   */
  const handleToggleCoverage = useCallback(
    (id: string, enabled: boolean) => {
      if (isMorteCoverageId(id, morteId)) return;
      const current = formApi.state.values.coverages;

      if (!enabled) {
        /*
         * Zera capital e percentual junto: linha desligada não entra no attach,
         * então manter o valor fazia dela uma base de cálculo fantasma para as
         * coberturas proporcionais (`getCoverageCapitalsByCode`). Não há perda —
         * reativar recalcula por `getInitialCoverageValues`.
         */
        formApi.setFieldValue('coverages', {
          ...current,
          [id]: {
            enabled: false,
            maxIndividualCapital: '',
            percent: '',
            benefitCode: '',
          },
        });
        return;
      }

      const catalogItem = catalogById.get(id);
      if (!catalogItem) return;

      const initial = getInitialCoverageValues(
        catalogItem,
        getLimitsContext(),
        formatCurrency
      );
      formApi.setFieldValue('coverages', {
        ...current,
        [id]: {
          enabled: true,
          maxIndividualCapital: initial.maxIndividualCapital,
          percent: initial.percent,
          benefitCode: '',
        },
      });
    },
    [morteId, catalogById, getLimitsContext, formApi]
  );

  /**
   * Escolha do benefício (coberturas com `benefitOptions`). O código escolhido
   * é o que vai no attach (`benefits: [...]`). Opção com valor em R$ alimenta
   * capital e % pelo MESMO caminho do input livre — tetos e derivações
   * continuam idênticos. Opção sem valor monetário (ex.: "30 Diárias"): a
   * opção só escolhe o número de diárias — o % continua sendo o input do
   * valor da diária (% do capital individual, limitado ao teto), então é
   * preservado; fora das diárias, zera capital e %.
   */
  const handleBenefitChange = useCallback(
    (id: string, benefitCode: string) => {
      if (isMorteCoverageId(id, morteId)) return;
      const catalogItem = catalogById.get(id);
      if (!catalogItem) return;

      const option = findBenefitOption(catalogItem, benefitCode);
      const current = formApi.state.values.coverages;
      const row = current[id] ?? {
        enabled: false,
        maxIndividualCapital: '',
        percent: '',
        benefitCode: '',
      };

      if (option?.capital == null) {
        const keepsPercent = usesBenefitSelectionWithPercent(catalogItem);
        const percent = keepsPercent ? row.percent : '';
        formApi.setFieldValue('coverages', {
          ...current,
          [id]: {
            ...row,
            benefitCode: option?.code ?? '',
            maxIndividualCapital: percent.trim()
              ? previewCapitalFromPercentDraft(
                  percent,
                  catalogItem,
                  getLimitsContext(),
                  formatCurrency
                )
              : '',
            percent,
          },
        });
        return;
      }

      const limitsCtx = getLimitsContext();
      const percentRef = getPercentReferenceCapital(catalogItem, limitsCtx);
      const limits = getEffectiveLimits(catalogItem, limitsCtx);
      const { capital: cappedCapital, percent } = capCapitalAndPercent({
        capital: option.capital,
        percentRefCapital: percentRef,
        effectiveMax: limits.max,
        effectiveMin: limits.min,
      });

      formApi.setFieldValue('coverages', {
        ...current,
        [id]: {
          ...row,
          benefitCode: option.code,
          maxIndividualCapital:
            cappedCapital > 0 ? formatCurrency(cappedCapital) : '',
          percent,
        },
      });
    },
    [morteId, catalogById, getLimitsContext, formApi]
  );

  /**
   * Sem clamp durante a digitação: aplicar os limites a cada tecla reescrevia
   * "R$ 0,10" para o mínimo antes de o corretor terminar de digitar. O valor
   * fica como digitado e a linha acusa a faixa permitida em tempo real
   * (`getVisibleErrors`); o schema segue bloqueando o submit fora da faixa.
   */
  const handleIndividualCapitalChange = useCallback(
    (id: string, value: string) => {
      if (isMorteCoverageId(id, morteId)) return;
      const catalogItem = catalogById.get(id);
      if (!catalogItem) return;

      const limitsCtx = getLimitsContext();
      const capital = parseCurrencyToNumber(value);
      const percentRef = getPercentReferenceCapital(catalogItem, limitsCtx);
      const percent =
        Number.isFinite(capital) && capital > 0 && percentRef > 0
          ? roundCoveragePercent((capital / percentRef) * 100)
          : '';

      const current = formApi.state.values.coverages;
      const row = current[id] ?? {
        enabled: false,
        maxIndividualCapital: '',
        percent: '',
      };

      formApi.setFieldValue('coverages', {
        ...current,
        [id]: {
          ...row,
          maxIndividualCapital: value,
          percent,
        },
      });
    },
    [morteId, catalogById, getLimitsContext, formApi]
  );

  const handlePercentChange = useCallback(
    (id: string, value: string) => {
      if (isMorteCoverageId(id, morteId)) return;
      const catalogItem = catalogById.get(id);
      if (!catalogItem) return;

      const limitsCtx = getLimitsContext();
      const previewCapital = value.trim()
        ? previewCapitalFromPercentDraft(
            value,
            catalogItem,
            limitsCtx,
            formatCurrency
          )
        : '';

      const current = formApi.state.values.coverages;
      const row = current[id] ?? {
        enabled: false,
        maxIndividualCapital: '',
        percent: '',
      };

      formApi.setFieldValue('coverages', {
        ...current,
        [id]: {
          ...row,
          percent: value,
          maxIndividualCapital: previewCapital,
        },
      });
    },
    [morteId, catalogById, getLimitsContext, formApi]
  );

  const handlePercentBlur = useCallback(
    (id: string) => {
      if (isMorteCoverageId(id, morteId)) return;
      const catalogItem = catalogById.get(id);
      if (!catalogItem) return;

      const current = formApi.state.values.coverages;
      const row = current[id];
      if (!row?.enabled) return;

      const committed = commitPercentAndCapital(
        row.percent,
        catalogItem,
        getLimitsContext(),
        formatCurrency,
        parseCurrencyToNumber(row.maxIndividualCapital)
      );

      formApi.setFieldValue('coverages', {
        ...current,
        [id]: { ...row, ...committed },
      });
    },
    [morteId, catalogById, getLimitsContext, formApi]
  );

  const handleToggleAssistance = useCallback(
    (code: string, enabled: boolean) => {
      const current = formApi.state.values.assistances;
      const row = current[code] ?? { enabled: false };
      formApi.setFieldValue('assistances', {
        ...current,
        [code]: { ...row, enabled },
      });
    },
    [formApi]
  );

  const handleCapitalizationChange = useCallback(
    (value: string) => {
      formApi.setFieldValue('capitalization', value);
    },
    [formApi]
  );

  const handleAssistanceFuneralChange = useCallback(
    (value: string) => {
      formApi.setFieldValue('assistanceFuneral', value);
    },
    [formApi]
  );

  const morteCapitalForValidation = useMemo(
    () => getMorteCapital(),
    [getMorteCapital]
  );

  const stepSchema = useMemo(
    () =>
      createStepCoveragesFormSchema(
        coverageCatalog,
        morteId,
        referenceCapital,
        morteCapitalForValidation,
        {
          groupCapitals: activeGroupCapitals,
          exclusionCatalogs: {
            assistanceCatalog,
            assistanceFuneralOptions,
            capitalizationOptions,
          },
        }
      ),
    [
      coverageCatalog,
      morteId,
      referenceCapital,
      morteCapitalForValidation,
      activeGroupCapitals,
      assistanceCatalog,
      assistanceFuneralOptions,
      capitalizationOptions,
    ]
  );

  const getVisibleErrors = useCallback(
    (values: StepCoveragesFormValues): StepCoveragesVisibleErrors => {
      /*
       * Conflito de exclusividade/dependência aparece em TEMPO REAL, sem
       * esperar a primeira tentativa de submit: é consequência direta de um
       * clique do corretor (ligar uma cobertura), não de campo pela metade —
       * segurar a mensagem até o submit faria ele descobrir tarde. Os demais
       * erros (valor, limite) seguem represados por `submitAttempted` para não
       * acusar campo que ainda está sendo preenchido.
       */
      const relationErrors: CoverageFieldErrors = {};
      for (const conflict of findCoverageRelationConflicts(
        coverageCatalog,
        id => Boolean(values.coverages[id]?.enabled)
      )) {
        relationErrors[conflict.coverageId] = {
          ...relationErrors[conflict.coverageId],
          selection: conflict.message,
        };
      }

      // Acoplamento de nível dependente↔pai (ex.: Assistência Funeral deve ter
      // o mesmo valor do Auxílio Funeral). Reação direta a uma escolha no
      // seletor — em tempo real, como os demais conflitos de relação.
      for (const conflict of findDependentCapitalConflicts(
        coverageCatalog,
        id => Boolean(values.coverages[id]?.enabled),
        id =>
          parseCurrencyToNumber(
            values.coverages[id]?.maxIndividualCapital ?? ''
          )
      )) {
        relationErrors[conflict.coverageId] = {
          ...relationErrors[conflict.coverageId],
          selection:
            relationErrors[conflict.coverageId]?.selection ?? conflict.message,
        };
      }

      // Exclusão mútua por ExclusionGroupCode — ADICIONAL ao RelationType e,
      // ao contrário dele, atravessa tipos: o conflito pode cair numa cobertura
      // E num benefício ao mesmo tempo (ex.: ACB_GROUP). Também é reação direta
      // a um clique, então aparece em tempo real, sem esperar o submit.
      /*
       * Capital fora da faixa [min, max] aparece em TEMPO REAL, junto com o
       * valor digitado intacto: como o input não ajusta mais o valor sozinho
       * (era o bug — digitar "10" virava o mínimo no meio da digitação), a
       * faixa permitida precisa estar visível enquanto o corretor digita. Os
       * limites são os EFETIVOS (`getEffectiveLimits`): o máximo já embute o
       * `proportion` sobre a cobertura de referência. Campo vazio segue
       * represado por `submitAttempted` — só acusa valor de fato preenchido.
       */
      const rangeLimitsCtx = buildEntityLimitsContext({
        coverageCatalog,
        morteCatalogItem,
        morteId,
        referenceCapital,
        morteCapital: resolveMorteCapitalForValidation(
          values,
          morteId,
          referenceCapital
        ),
        rows: values.coverages,
      });
      const rangeErrors: CoverageFieldErrors = {};
      for (const catalogItem of coverageCatalog) {
        const row = values.coverages[catalogItem.id];
        if (!row?.enabled || isMorteCoverageId(catalogItem.id, morteId)) {
          continue;
        }
        // Capital escolhido por seletor de benefício: não há input livre.
        if (usesBenefitSelection(catalogItem)) continue;

        const capital = parseCurrencyToNumber(row.maxIndividualCapital);
        if (!Number.isFinite(capital) || capital <= 0) continue;

        const limits = getEffectiveLimits(catalogItem, rangeLimitsCtx);
        if (isEffectiveRangeInvalid(limits)) continue;

        const belowMin = limits.min > 0 && capital < limits.min;
        const aboveMax = limits.max > 0 && capital > limits.max;
        if (belowMin || aboveMax) {
          rangeErrors[catalogItem.id] = {
            maxIndividualCapital: getCapitalOutOfRangeMessage(
              catalogItem,
              limits
            ),
          };
        }
      }

      const exclusionConflicts = findCapitalGlobalExclusionConflicts(values, {
        coverageCatalog,
        assistanceCatalog,
        assistanceFuneralOptions,
        capitalizationOptions,
      });
      const assistanceErrors: Record<string, string | undefined> = {};
      let exclusionFuneralError: string | undefined;
      let exclusionCapitalizationError: string | undefined;
      for (const conflict of exclusionConflicts) {
        routeExclusionConflict(conflict, {
          coverage: (id, message) => {
            relationErrors[id] = {
              ...relationErrors[id],
              selection: relationErrors[id]?.selection ?? message,
            };
          },
          assistance: (code, message) => {
            assistanceErrors[code] ??= message;
          },
          assistanceFuneral: message => {
            exclusionFuneralError ??= message;
          },
          capitalization: message => {
            exclusionCapitalizationError ??= message;
          },
        });
      }

      if (!submitAttempted) {
        return {
          coverageErrors: mergeCoverageFieldErrors(rangeErrors, relationErrors),
          assistanceErrors,
          capitalizationError: exclusionCapitalizationError,
          assistanceFuneralError: exclusionFuneralError,
          generalError: undefined,
        };
      }

      const validation = stepSchema.safeParse(values);
      const parsedErrors = validation.success
        ? {
            coverageErrors: {} as CoverageFieldErrors,
            capitalizationError: undefined,
            assistanceFuneralError: undefined,
          }
        : buildStepCoveragesErrors(validation.error.issues);

      return {
        // A mensagem de faixa sobrepõe o erro de min/max do schema no mesmo
        // campo: é a mesma violação, com a redação que mostra os dois limites.
        coverageErrors: mergeCoverageFieldErrors(
          mergeCoverageFieldErrors(
            mergeCoverageFieldErrors(parsedErrors.coverageErrors, rangeErrors),
            relationErrors
          ),
          apiCoverageErrors
        ),
        assistanceErrors,
        // Conflito de exclusão tem precedência: é reação direta ao clique do
        // corretor, mais acionável que um erro de valor/limite do schema.
        capitalizationError:
          exclusionCapitalizationError ?? parsedErrors.capitalizationError,
        assistanceFuneralError:
          exclusionFuneralError ?? parsedErrors.assistanceFuneralError,
        generalError: generalApiError,
      };
    },
    [
      stepSchema,
      submitAttempted,
      apiCoverageErrors,
      generalApiError,
      coverageCatalog,
      assistanceCatalog,
      assistanceFuneralOptions,
      capitalizationOptions,
      morteCatalogItem,
      morteId,
      referenceCapital,
    ]
  );

  const validateAllEntities = useCallback((): EntityCoveragesValidation => {
    const entitiesToCheck = entities.length > 0 ? entities : [];

    if (entitiesToCheck.length <= 1) {
      const committedCoverages = commitCoveragesPercentDrafts(
        formApi.state.values.coverages,
        catalogById,
        getLimitsContext(),
        morteId
      );
      formApi.setFieldValue('coverages', committedCoverages);
      const result = stepSchema.safeParse({
        ...formApi.state.values,
        coverages: committedCoverages,
      });
      if (result.success) {
        return { valid: true, messages: [] };
      }
      return {
        valid: false,
        messages: collectStepCoveragesErrorMessages(result.error.issues),
      };
    }

    const allValues = captureAllEntities();
    const committedMap: CoveragesDataMap = { ...allValues };
    const messages: string[] = [];
    let firstInvalidEntityId: string | undefined;

    for (const entity of entitiesToCheck) {
      const key = normalizeEntityDoc(entity.id);
      const values = allValues[key];

      /*
       * Estipulante cuja aba nunca foi aberta não tem estado: só a entidade
       * ativa é hidratada (fora de um resync completo). Antes disto,
       * `values.coverages` estourava TypeError dentro do handler de submit e o
       * botão "Próximo" simplesmente não reagia.
       */
      if (!values) {
        firstInvalidEntityId ??= entity.id;
        messages.push(
          `${entity.label}: abra a aba deste estipulante e preencha as coberturas.`
        );
        continue;
      }

      const entityCatalogs = getCatalogsForEntity(entity.id);
      const entityReferenceCapital = getReferenceCapitalForEntity(
        proposalDetails,
        entity.id
      );
      const entityGroupCapitals = getGroupReferenceCapitals(
        findRiskProfileForEntity(proposalDetails, entity.id)
      );
      const entityMorteCapital = resolveMorteCapitalForValidation(
        values,
        morteId,
        entityReferenceCapital
      );
      const entityLimitsCtx = buildEntityLimitsContext({
        coverageCatalog: entityCatalogs.coverageCatalog,
        morteCatalogItem,
        morteId,
        referenceCapital: entityReferenceCapital,
        morteCapital: entityMorteCapital,
        rows: values.coverages,
      });
      const committedValues: CoveragesEntityValues = {
        ...values,
        coverages: commitCoveragesPercentDrafts(
          values.coverages,
          buildCatalogById(entityCatalogs.coverageCatalog),
          entityLimitsCtx,
          morteId
        ),
      };
      committedMap[key] = committedValues;

      const entityValidation = validateEntityCoverages(
        committedValues,
        entityReferenceCapital,
        entityCatalogs.coverageCatalog,
        morteId,
        entityGroupCapitals,
        {
          assistanceCatalog: entityCatalogs.assistanceCatalog,
          assistanceFuneralOptions: entityCatalogs.assistanceFuneralOptions,
          capitalizationOptions: entityCatalogs.capitalizationOptions,
        }
      );

      if (!entityValidation.valid) {
        firstInvalidEntityId ??= entity.id;
        // Prefixo com o nome do estipulante: sem ele o corretor lia "Informe o
        // percentual de X" sem saber em qual aba.
        for (const message of entityValidation.messages) {
          messages.push(`${entity.label}: ${message}`);
        }
      }
    }

    /*
     * Persiste o que foi de fato validado. Sem isto os valores confirmados
     * viviam só numa cópia local e a tela seguia mostrando os rascunhos — o
     * toast descrevia números que não estavam em lugar nenhum.
     */
    applyEntityValues(committedMap);

    if (firstInvalidEntityId) {
      handleEntityTabChange(firstInvalidEntityId);
      return { valid: false, messages };
    }

    return { valid: true, messages: [] };
  }, [
    entities,
    stepSchema,
    formApi.state.values,
    formApi.setFieldValue,
    captureAllEntities,
    applyEntityValues,
    proposalDetails,
    getCatalogsForEntity,
    catalogById,
    morteCatalogItem,
    morteId,
    getLimitsContext,
    handleEntityTabChange,
  ]);

  const handleSubmit = useCallback(() => {
    clearAttachValidationErrors();
    setSubmitAttempted(true);

    // Formato PME: sem toast de validação — o avanço só é bloqueado e cada
    // linha inválida mostra seu erro inline (a aba já troca para o primeiro
    // estipulante inválido dentro de `validateAllEntities`).
    const validation = validateAllEntities();
    if (!validation.valid) {
      return;
    }

    void submitToApi();
  }, [clearAttachValidationErrors, validateAllEntities, submitToApi]);

  const handleEntityTabChangeWithReset = useCallback(
    (newEntityId: string) => {
      const committedCoverages = commitCoveragesPercentDrafts(
        formApi.state.values.coverages,
        catalogById,
        getLimitsContext(),
        morteId
      );
      formApi.setFieldValue('coverages', committedCoverages);
      setSubmitAttempted(false);
      clearAttachValidationErrors();
      handleEntityTabChange(newEntityId);
    },
    [
      catalogById,
      clearAttachValidationErrors,
      formApi,
      getLimitsContext,
      handleEntityTabChange,
      morteId,
    ]
  );

  const retryCatalog = useCallback(() => {
    void refetchCatalog();
  }, [refetchCatalog]);

  return {
    coverageCatalog,
    assistanceCatalog,
    assistanceFuneralOptions,
    capitalizationOptions,
    isCapitalizationAvailable,
    isCatalogLoading,
    hasCatalogError: isCatalogError,
    retryCatalog,
    morteId,
    handleToggleCoverage,
    handleIndividualCapitalChange,
    handleBenefitChange,
    handlePercentChange,
    handlePercentBlur,
    handleToggleAssistance,
    handleCapitalizationChange,
    handleAssistanceFuneralChange,
    handleSubmit,
    handleEntityTabChangeWithReset,
    isPending,
    getVisibleErrors,
  };
}
